import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Settings, 
  Database, 
  RefreshCw, 
  Trash2, 
  AlertTriangle,
  CheckCircle,
  Loader2,
  Car,
  LayoutDashboard,
  Users,
  Store,
  Monitor,
  FileText,
  TrendingUp,
  LogOut,
  BarChart3,
  Gift,
  ListChecks,
  Megaphone,
  CreditCard,
  Info,
  Sparkles,
  Activity,
  Wallet,
  DollarSign,
  Package
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";

const API = '/api';

// Sidebar navigation - same as other admin pages
const navItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/admin/dashboard" },
  { icon: Users, label: "Registrations", path: "/admin/registrations" },
  { icon: AlertTriangle, label: "OCR Exceptions", path: "/admin/ocr-exceptions" },
  { icon: Store, label: "Retailers", path: "/admin/retailers" },
  { icon: Gift, label: "Rewards", path: "/admin/rewards" },
  { icon: BarChart3, label: "Analytics", path: "/admin/analytics" },
  { icon: Monitor, label: "Screens", path: "/admin/screens" },
  { icon: ListChecks, label: "Playlists", path: "/admin/playlists" },
  { icon: FileText, label: "Content", path: "/admin/content" },
  { icon: Megaphone, label: "Advertisers", path: "/admin/advertisers" },
  { icon: TrendingUp, label: "Campaigns", path: "/admin/campaigns" },
  { icon: CreditCard, label: "Billing", path: "/admin/billing" },
  { icon: TrendingUp, label: "Revenue", path: "/admin/revenue" },
  { icon: Wallet, label: "Commercial", path: "/admin/commercial" },
  { icon: DollarSign, label: "Finance Centre", path: "/admin/commercial/finance" },
  { icon: Package, label: "Products & Tariffs", path: "/admin/commercial/products-tariffs" },
  { icon: Store, label: "Sites & Kiosks", path: "/admin/sites" },
  { icon: FileText, label: "Audit Log", path: "/admin/audit-log" },
  { icon: Settings, label: "Settings", path: "/admin/settings" },
  { icon: Activity, label: "System Status", path: "/admin/system-status" },
];

export default function AdminSettings() {
  const navigate = useNavigate();
  const [demoStatus, setDemoStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [seedingData, setSeedingData] = useState(false);
  const [clearingData, setClearingData] = useState(false);
  const [lastSeedResult, setLastSeedResult] = useState(null);
  const [ayrMilesLoading, setAyrMilesLoading] = useState(false);
  const [ayrMilesResult, setAyrMilesResult] = useState(null);
  const [showAyrMilesConfirm, setShowAyrMilesConfirm] = useState(false);

  useEffect(() => {
    fetchDemoStatus();
  }, []);

  const fetchDemoStatus = async () => {
    try {
      const response = await axios.get(`${API}/demo/status`);
      setDemoStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch demo status:", error);
    }
  };

  const handleSeedData = async () => {
    setSeedingData(true);
    try {
      const response = await axios.post(`${API}/demo/seed`);
      setLastSeedResult(response.data.counts);
      toast.success("Demo data seeded successfully!");
    } catch (error) {
      console.error("Failed to seed demo data:", error);
      toast.error("Failed to seed demo data");
    } finally {
      setSeedingData(false);
    }
  };

  const handleResetData = async () => {
    if (!confirm("This will clear and reseed ALL demo data. Continue?")) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/demo/reset`);
      setLastSeedResult(response.data.counts);
      toast.success("Demo data reset successfully!");
    } catch (error) {
      console.error("Failed to reset demo data:", error);
      toast.error("Failed to reset demo data");
    } finally {
      setLoading(false);
    }
  };

  const handleClearData = async () => {
    if (!confirm("This will DELETE all demo data. This cannot be undone. Continue?")) return;
    
    setClearingData(true);
    try {
      await axios.delete(`${API}/demo/clear`);
      setLastSeedResult(null);
      toast.success("Demo data cleared successfully!");
    } catch (error) {
      console.error("Failed to clear demo data:", error);
      toast.error("Failed to clear demo data");
    } finally {
      setClearingData(false);
    }
  };

  const handleResetAyrMilesDemo = async () => {
    setShowAyrMilesConfirm(false);
    setAyrMilesLoading(true);
    try {
      const response = await axios.post(`${API}/demo/reset-ayr-miles`);
      setAyrMilesResult(response.data);
      toast.success("Ayr Miles demo data reset successfully!", {
        description: "6 demo vehicles ready for demonstration"
      });
    } catch (error) {
      console.error("Failed to reset Ayr Miles demo:", error);
      toast.error("Failed to reset Ayr Miles demo data", {
        description: error.response?.data?.detail || "Please try again"
      });
    } finally {
      setAyrMilesLoading(false);
    }
  };

  const handleLogout = () => {
    document.cookie = "admin_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-settings">
      {/* Ayr Miles Confirmation Dialog */}
      <AlertDialog open={showAyrMilesConfirm} onOpenChange={setShowAyrMilesConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Reset Ayr Miles Demo Data
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will clear existing Ayr Miles demo data and create fresh demo scenarios with 6 memorable vehicle registrations (AYR 1A through AYR 6F).
              <br /><br />
              <strong>Demo scenarios include:</strong>
              <ul className="list-disc list-inside mt-2 text-sm">
                <li>AYR 1A - Free parking (2hr with entitlement)</li>
                <li>AYR 2B - Partial discount (4.5hr with entitlement)</li>
                <li>AYR 3C - No discount (browse only)</li>
                <li>AYR 4D - Completed session (audit trail)</li>
                <li>AYR 5E - Suspicious pattern (fraud demo)</li>
                <li>AYR 6F - Expired entitlement</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleResetAyrMilesDemo}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Reset Ayr Miles Demo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Sidebar */}
      <div className="w-64 bg-white border-r border-slate-200 flex flex-col">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
              <Car className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900">ParkWise</h1>
              <p className="text-xs text-slate-500">Admin Console</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.path === "/admin/settings";
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <Icon className="w-5 h-5" />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-200">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900">Settings</h1>
            <p className="text-slate-500 mt-1">Manage demo data and system settings</p>
          </div>

          {/* Demo Mode Status */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-600" />
                Demo Mode Status
              </CardTitle>
              <CardDescription>
                Demo mode controls whether demonstration data features are enabled
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                {demoStatus?.demo_mode ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Demo Mode Enabled</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-slate-500">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-medium">Demo Mode Disabled</span>
                  </div>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-2">
                Set DEMO_MODE=true in environment variables to enable demo features
              </p>
            </CardContent>
          </Card>

          {/* Ayr Miles Demo - Priority Section */}
          <Card className="mb-6 border-purple-200 bg-gradient-to-br from-purple-50 to-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                Ayr Miles Demo
              </CardTitle>
              <CardDescription>
                Reset demo data specifically for Ayr Miles parking entitlement demonstrations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-purple-200">
                <div>
                  <h3 className="font-semibold text-purple-900">Reset Ayr Miles Demo</h3>
                  <p className="text-sm text-purple-700">
                    Creates 6 demo vehicles (AYR 1A - AYR 6F) with parking scenarios for investor demos
                  </p>
                </div>
                <Button
                  onClick={() => setShowAyrMilesConfirm(true)}
                  disabled={ayrMilesLoading}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="reset-ayr-miles-btn"
                >
                  {ayrMilesLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Reset Ayr Miles Demo
                    </>
                  )}
                </Button>
              </div>

              {/* Ayr Miles Result */}
              {ayrMilesResult && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <h3 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Demo Data Ready
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                    <div className="text-center p-2 bg-white rounded-lg">
                      <p className="text-xl font-bold text-purple-600">{ayrMilesResult.counts?.retailers || 6}</p>
                      <p className="text-xs text-slate-500">Retailers</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded-lg">
                      <p className="text-xl font-bold text-purple-600">{ayrMilesResult.counts?.vehicles || 6}</p>
                      <p className="text-xs text-slate-500">Vehicles</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded-lg">
                      <p className="text-xl font-bold text-purple-600">{ayrMilesResult.counts?.entitlements || 5}</p>
                      <p className="text-xs text-slate-500">Entitlements</p>
                    </div>
                  </div>
                  <div className="text-sm text-purple-800">
                    <p className="font-medium mb-2">Demo Vehicles:</p>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      {ayrMilesResult.demo_vehicles?.map(v => (
                        <div key={v.reg} className="flex items-center gap-2">
                          <span className="font-mono font-bold">{v.reg}</span>
                          <span className="text-purple-600">{v.name}</span>
                        </div>
                      )) || (
                        <>
                          <div>AYR 1A - Sarah (FREE)</div>
                          <div>AYR 2B - James (£3.00)</div>
                          <div>AYR 3C - Emma (£6.00)</div>
                          <div>AYR 4D - David (Audit)</div>
                          <div>AYR 5E - Karen (Fraud)</div>
                          <div>AYR 6F - Michael (Expired)</div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Demo Data Management */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5 text-purple-600" />
                Demo Data Management
              </CardTitle>
              <CardDescription>
                Create, reset, or clear demonstration data for platform demos
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Seed Data */}
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                <div>
                  <h3 className="font-semibold text-green-900">Seed Demo Data</h3>
                  <p className="text-sm text-green-700">
                    Creates realistic demo data (sites, retailers, registrations, etc.)
                  </p>
                </div>
                <Button
                  onClick={handleSeedData}
                  disabled={seedingData}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {seedingData ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Seeding...
                    </>
                  ) : (
                    <>
                      <Database className="w-4 h-4 mr-2" />
                      Seed Data
                    </>
                  )}
                </Button>
              </div>

              {/* Reset Data */}
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div>
                  <h3 className="font-semibold text-blue-900">Reset Demo Data</h3>
                  <p className="text-sm text-blue-700">
                    Clears existing demo data and creates fresh demo data
                  </p>
                </div>
                <Button
                  onClick={handleResetData}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Reset Data
                    </>
                  )}
                </Button>
              </div>

              {/* Clear Data */}
              <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
                <div>
                  <h3 className="font-semibold text-red-900">Clear Demo Data</h3>
                  <p className="text-sm text-red-700">
                    Removes all demo data from the database (cannot be undone)
                  </p>
                </div>
                <Button
                  onClick={handleClearData}
                  disabled={clearingData}
                  variant="destructive"
                  className="bg-red-600 hover:bg-red-700"
                >
                  {clearingData ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Clearing...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Clear Data
                    </>
                  )}
                </Button>
              </div>

              {/* Last Seed Result */}
              {lastSeedResult && (
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-3">Last Seed Result</h3>
                  <div className="grid grid-cols-3 gap-4">
                    {Object.entries(lastSeedResult).map(([key, value]) => (
                      <div key={key} className="text-center">
                        <p className="text-2xl font-bold text-blue-600">{value}</p>
                        <p className="text-xs text-slate-500 capitalize">{key}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Demo Data Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Demo Data Contents</CardTitle>
              <CardDescription>
                When seeded, demo data includes the following
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">5</p>
                  <p className="text-sm text-slate-500">Sites</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">20</p>
                  <p className="text-sm text-slate-500">Kiosks</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">25</p>
                  <p className="text-sm text-slate-500">Retailers</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">50</p>
                  <p className="text-sm text-slate-500">Registrations</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">100</p>
                  <p className="text-sm text-slate-500">QR Visits</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">10</p>
                  <p className="text-sm text-slate-500">Advertisers</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">20</p>
                  <p className="text-sm text-slate-500">Campaigns</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">10</p>
                  <p className="text-sm text-slate-500">Screens</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
